import { useEffect, useRef, useState } from "react";

/**
 * One sentence when the number of handovers waiting for me grows (UX ruling
 * 12, F-16) — the badge changes without a word, and the list is polled once
 * a minute. The first answer is not news; a falling number is not either.
 * The count is part of the sentence, so a second arrival is a new text and
 * is read out again. Set from a timer, never synchronously in the effect.
 *
 * A falling number takes the sentence back instead of saying anything: the
 * region is a place a screen reader can also walk to, and „— 3 warten" there
 * while none wait would be a stale count (rendered review, Task 14). Emptying
 * a live region is not read out.
 *
 * More than one at once gets its own wording (fix round 1, Task 14): „Neue
 * Übergabe" reads as exactly one, so several arriving together — the poll
 * runs once a minute, more than one can easily land between two polls —
 * says „Übergaben" and states the count instead of implying a single one.
 */
export function useArrivalAnnouncement(count: number | null): string {
  const seen = useRef<number | null>(null);
  const [said, setSaid] = useState("");
  useEffect(() => {
    const before = seen.current;
    seen.current = count;
    if (before === null || count === null || count === before) return undefined;
    const grown = count - before;
    const sentence =
      count < before
        ? ""
        : grown > 1
          ? `Neue Übergaben an dich — ${count} warten.`
          : count === 1
            ? "Neue Übergabe an dich."
            : `Neue Übergabe an dich — ${count} warten.`;
    const timer = window.setTimeout(() => setSaid(sentence), 0);
    return () => window.clearTimeout(timer);
  }, [count]);
  return said;
}
